import type { HiddenCoinView, SituationView } from "@screener/protocol";
import type { PinnedCards } from "./cards";
import type { OutcomeEntry } from "./stream-state";

/**
 * Скрытые монеты §6.10 на стороне клиента (§75): отбор по приведённой базе, а
 * не по паре, поэтому монета уходит на всех биржах, рынках и котировках разом.
 * Признака скрытия ни в снимке, ни в потоке §7.6 нет — демон наблюдает и пишет
 * скрытую монету как прежде (§55 п. 3), прячет её только экран.
 *
 * Чистый модуль без React — тем же приёмом, что `favorite-match.ts`: им
 * пользуются экран и хук, а тесты — без рендера.
 */

/** Состояние списка скрытых монет — тот же снаряд, что у избранного. */
export type HiddenCoinsState =
  | { status: "loading" }
  | { status: "ready"; coins: HiddenCoinView[] }
  | { status: "unavailable" };

const NO_HIDDEN: ReadonlySet<string> = new Set();

/**
 * Базы, которые нужно убрать с экрана. Пока список не прочитан или прочитать
 * его не удалось, множество пустое: ошибка хранилища не прячет данные (спеки
 * `web-density-*`), и отбор в этом случае просто не действует.
 */
export function hiddenBasesOf(state: HiddenCoinsState): ReadonlySet<string> {
  if (state.status !== "ready" || state.coins.length === 0) {
    return NO_HIDDEN;
  }
  return new Set(state.coins.map((coin) => coin.base));
}

export function isHiddenBase(base: string, hidden: ReadonlySet<string>): boolean {
  return hidden.has(base);
}

/** Строки без скрытых монет; нечего скрывать — тот же массив, без копии. */
export function withoutHiddenRows<T extends { instrument: { base: string } }>(
  rows: readonly T[],
  hidden: ReadonlySet<string>,
): readonly T[] {
  if (hidden.size === 0) {
    return rows;
  }
  return rows.filter((row) => !isHiddenBase(row.instrument.base, hidden));
}

/**
 * Ситуация §5 — это одна монета: ситуация скрытой монеты уходит целиком, а не
 * остаётся строкой со всеми участниками, скрытыми отбором.
 */
export function withoutHiddenSituations(
  situations: SituationView[],
  hidden: ReadonlySet<string>,
): SituationView[] {
  if (hidden.size === 0) {
    return situations;
  }
  return situations.filter((situation) => !isHiddenBase(situation.key.base, hidden));
}

/**
 * Закрепления карточек без скрытых монет: скрытие сильнее закрепления (§75
 * п. 4). Само закрепление в базе остаётся — после возврата монеты карточка
 * снова встаёт среди закреплённых.
 */
export function withoutHiddenPinned(
  pinned: PinnedCards | null,
  hidden: ReadonlySet<string>,
): PinnedCards | null {
  if (pinned === null || hidden.size === 0) {
    return pinned;
  }
  return {
    keys: new Map(
      [...pinned.keys].filter(([, instrument]) => !isHiddenBase(instrument.base, hidden)),
    ),
    allRows: withoutHiddenRows(pinned.allRows, hidden),
  };
}

/** Лента недавних исходов без записей скрытых монет. */
export function withoutHiddenOutcomes(
  outcomes: readonly OutcomeEntry[],
  hidden: ReadonlySet<string>,
): readonly OutcomeEntry[] {
  return withoutHiddenRows(outcomes, hidden);
}

function byBase(left: HiddenCoinView, right: HiddenCoinView): number {
  return left.base < right.base ? -1 : left.base > right.base ? 1 : 0;
}

/**
 * Ответ на скрытие ложится в готовый список без перечитывания. Повтор той же
 * монеты не дублирует запись: демон идемпотентен и отдаёт прежний `created_at`.
 * Не прочитанный список не трогается — класть в него нечего.
 */
export function applyHide(state: HiddenCoinsState, coin: HiddenCoinView): HiddenCoinsState {
  if (state.status !== "ready") {
    return state;
  }
  if (state.coins.some((existing) => existing.base === coin.base)) {
    return state;
  }
  return { status: "ready", coins: [...state.coins, coin].sort(byBase) };
}

/** Ответ на возврат убирает монету из готового списка; прочие остаются. */
export function applyUnhide(state: HiddenCoinsState, base: string): HiddenCoinsState {
  if (state.status !== "ready") {
    return state;
  }
  return { status: "ready", coins: state.coins.filter((coin) => coin.base !== base) };
}
